import React, { useEffect, useState } from 'react';

import {
  formatAadharDisplayOrFallback,
  formatMobileDisplayOrFallback,
  sanitizeAadharInput,
  sanitizeMobileInput,
  validateAadharNumber,
  validateMobileNumber,
} from './formatDisplay';

const emptyOwnerForm = () => ({
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  aadharNo: '',
});

const DetailField = ({ label, value, wide = false }) => (
  <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
    <span className="restaurant-detail-label">{label}</span>
    <span className="restaurant-detail-value">{value != null && String(value).trim() ? value : '—'}</span>
  </div>
);

const OwnerDetailsSection = ({
  title = 'Owner',
  owner,
  loading = false,
  canEdit = false,
  extraViewFields = [],
  onSave,
  emptyHeader = null,
  emptyBody = null,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyOwnerForm);

  useEffect(() => {
    setIsEditing(false);
    setForm({
      firstName: owner?.firstName || '',
      lastName: owner?.lastName || '',
      email: owner?.email || '',
      mobile: sanitizeMobileInput(owner?.mobile),
      aadharNo: sanitizeAadharInput(owner?.aadharNo),
    });
  }, [owner]);

  const handleSave = async () => {
    if (!onSave || !owner?.id) return;
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim();
    const mobile = sanitizeMobileInput(form.mobile);
    const aadharNo = sanitizeAadharInput(form.aadharNo);
    if (!firstName || !lastName) {
      window.alert('First name and last name are required.');
      return;
    }
    const mobileError = validateMobileNumber(mobile, { label: 'Personal Mobile' });
    if (mobileError) {
      window.alert(mobileError);
      return;
    }
    const aadharError = validateAadharNumber(aadharNo);
    if (aadharError) {
      window.alert(aadharError);
      return;
    }
    setSaving(true);
    try {
      await onSave({
        firstName,
        lastName,
        email,
        mobile,
        aadharNo,
        name: `${firstName} ${lastName}`.trim(),
        contactNumber: mobile,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Owner update failed:', error);
      window.alert(error?.response?.data?.message || 'Failed to update owner.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="restaurant-detail-card">
      <div className="restaurant-detail-card-header">
        <h3 className="restaurant-detail-section-title">{title}</h3>
        <div className="restaurant-detail-card-header-actions">
          {!isEditing && owner?.status ? (
            <span
              className={`restaurant-detail-badge restaurant-detail-badge-${String(owner.status)
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '-')}`}
            >
              {owner.status}
            </span>
          ) : null}
          {owner && canEdit && !isEditing ? (
            <button type="button" className="eats-admin-header-btn" onClick={() => setIsEditing(true)}>
              Edit
            </button>
          ) : null}
          {owner && canEdit && isEditing ? (
            <>
              <button
                type="button"
                className="eats-admin-header-btn"
                onClick={() => setIsEditing(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="eats-admin-header-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : null}
          {!owner && !loading ? emptyHeader : null}
        </div>
      </div>

      {loading ? <p className="restaurant-detail-message">Loading owner…</p> : null}

      {!loading && owner && isEditing ? (
        <div className="restaurant-detail-edit-form">
          <div className="get-restaurant-form-row flex">
            <div className="get-restaurant-form-group">
              <label>First Name</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm((current) => ({ ...current, firstName: e.target.value }))}
              />
            </div>
            <div className="get-restaurant-form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm((current) => ({ ...current, lastName: e.target.value }))}
              />
            </div>
            <div className="get-restaurant-form-group">
              <label>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))}
              />
            </div>
          </div>
          <div className="get-restaurant-form-row flex">
            <div className="get-restaurant-form-group">
              <label>Personal Mobile</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={form.mobile}
                onChange={(e) =>
                  setForm((current) => ({ ...current, mobile: sanitizeMobileInput(e.target.value) }))
                }
              />
            </div>
            <div className="get-restaurant-form-group">
              <label>Aadhar Number</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={14}
                value={form.aadharNo}
                onChange={(e) =>
                  setForm((current) => ({ ...current, aadharNo: sanitizeAadharInput(e.target.value) }))
                }
              />
            </div>
          </div>
        </div>
      ) : null}

      {!loading && owner && !isEditing ? (
        <div className="restaurant-detail-grid">
          <DetailField label="First Name" value={owner.firstName} />
          <DetailField label="Last Name" value={owner.lastName} />
          <DetailField label="Email" value={owner.email} />
          <DetailField label="Mobile" value={formatMobileDisplayOrFallback(owner.mobile)} />
          <DetailField label="Aadhar Number" value={formatAadharDisplayOrFallback(owner.aadharNo)} />
          {extraViewFields.map((field) => (
            <DetailField key={field.label} label={field.label} value={field.value} />
          ))}
        </div>
      ) : null}

      {!loading && !owner ? emptyBody : null}
    </div>
  );
};

export default OwnerDetailsSection;
